import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, AlertTriangle, Tag, Loader2, AlertCircle, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { 
  useGetNomenclatureDeviationsQuery,
  type NomenclatureDeviationItem,
} from '../../redux/api/qaApi';
import { useAppSelector } from '../../redux/hooks';

const NomenclatureTab: React.FC = () => {
  // Get site_id from Redux
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const siteId = currentSite?.site_id;

  const [search, setSearch] = useState('');
  const [equipmentFilter, setEquipmentFilter] = useState('');
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);
  const equipmentDropdownRef = useRef<HTMLDivElement>(null);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!equipmentDropdownRef.current?.contains(target)) setEquipmentDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);

  // Fetch nomenclature deviations
  const { data: deviationsData, isLoading, error } = useGetNomenclatureDeviationsQuery(
    siteId ? { site_id: siteId } : undefined,
    { skip: !siteId }
  );

  const deviations = deviationsData?.items || [];
  const equipmentTypeCounts = deviationsData?.equipment_type_counts || {};

  // Get unique equipment types
  const equipmentTypes = useMemo(() => {
    const types = new Set<string>();
    deviations.forEach((d) => types.add(d.equipment_type));
    return Array.from(types).sort();
  }, [deviations]);

  // Filter and group deviations
  const filteredDeviations = useMemo(() => {
    let filtered = deviations;

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (d) =>
          d.label.toLowerCase().includes(searchLower) ||
          d.issue.toLowerCase().includes(searchLower) ||
          d.equipment_type.toLowerCase().includes(searchLower)
      );
    }

    if (equipmentFilter) {
      filtered = filtered.filter((d) => d.equipment_type === equipmentFilter);
    }

    return filtered;
  }, [deviations, search, equipmentFilter]);

  // Paginate filtered deviations
  const totalItems = filteredDeviations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = totalItems > 0 ? offset + 1 : 0;
  const endItem = Math.min(offset + itemsPerPage, totalItems);
  const paginatedDeviations = filteredDeviations.slice(offset, offset + itemsPerPage);

  // Pagination handlers
  const handleNextPage = () => {
    if (offset + itemsPerPage < totalItems) {
      const newOffset = offset + itemsPerPage;
      setOffset(newOffset);
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (offset > 0) {
      const newOffset = Math.max(0, offset - itemsPerPage);
      setOffset(newOffset);
      setCurrentPage(prev => Math.max(1, prev - 1));
    }
  };

  const handlePageChange = (page: number) => {
    const newOffset = (page - 1) * itemsPerPage;
    setOffset(newOffset);
    setCurrentPage(page);
  };

  // Reset to first page when site, search, or filter changes
  useEffect(() => {
    setOffset(0);
    setCurrentPage(1);
  }, [siteId, search, equipmentFilter]);

  return (
    <div className="eg-page-shell flex flex-col">
      {/* Toolbar Header */}
      <div className="eg-toolbar-header">
        <div className="flex items-center justify-between mb-4 px-6 pt-4">
          <div>
            <h2 className="text-xl font-semibold eg-text-primary mb-1">Nomenclature Validation</h2>
            <p className="text-sm eg-text-secondary">Equipment naming and labeling deviations</p>
          </div>
          {deviationsData && (
            <div className="flex items-center gap-2 px-3 py-2 bg-orange-400/20 text-orange-400 rounded-lg border border-orange-400/30">
              <AlertTriangle size={18} />
              <span className="font-medium">{deviationsData.total} deviations found</span>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 px-6 pb-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 eg-text-secondary" />
              <input
                type="text"
                placeholder="Search deviations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg eg-text-primary placeholder-[var(--color-text-secondary)] focus:ring-2 focus:ring-engramiq-green focus:border-transparent"
              />
            </div>
          </div>

          {/* Equipment Type Filter - custom dropdown like Sidebar */}
          <div className="relative min-w-[200px]" ref={equipmentDropdownRef}>
            <button
              type="button"
              onClick={() => setEquipmentDropdownOpen((o) => !o)}
              className="w-full px-3 py-2 pr-8 bg-[var(--color-input-bg)] hover:bg-engramiq-green/20 rounded-lg eg-text-primary text-sm text-left focus:outline-none transition-all truncate"
            >
              {equipmentFilter || 'All Equipment Types'}
            </button>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-engramiq-green pointer-events-none" />
            {equipmentDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-bg-primary)] rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto flex flex-col border border-[var(--color-border)]">
                <button
                  type="button"
                  onClick={() => {
                    setEquipmentFilter('');
                    setEquipmentDropdownOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-surface-hover)] transition-colors ${
                    !equipmentFilter ? 'bg-engramiq-green/20 eg-text-primary' : 'eg-text-primary'
                  }`}
                >
                  All Equipment Types
                </button>
                {equipmentTypes.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      setEquipmentFilter(type);
                      setEquipmentDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-surface-hover)] transition-colors ${
                      equipmentFilter === type ? 'bg-engramiq-green/20 eg-text-primary' : 'eg-text-primary'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="eg-page-content flex-1 overflow-auto">
        {!siteId ? (
          <div className="flex items-center justify-center py-12">
            <AlertCircle className="w-8 h-8 eg-text-secondary mr-3" />
            <div className="eg-text-primary">
              <p className="font-semibold">No Site Selected</p>
              <p className="text-sm eg-text-secondary mt-1">
                Please select a site from the sidebar to view nomenclature deviations
              </p>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-engramiq-green" />
            <span className="ml-3 eg-text-primary">Loading nomenclature deviations...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <AlertCircle className="w-8 h-8 text-accent-red mr-3" />
            <div className="eg-text-primary">
              <p className="font-semibold">Error loading nomenclature deviations</p>
              <p className="text-sm eg-text-secondary mt-1">
                {error && 'data' in error ? JSON.stringify(error.data) : 'Unknown error'}
              </p>
            </div>
          </div>
        ) : deviations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Tag className="w-16 h-16 eg-text-secondary mb-4" />
            <p className="text-lg eg-text-primary mb-2">No Nomenclature Deviations Found</p>
            <p className="text-sm eg-text-secondary">
              All equipment labels are consistent.
            </p>
          </div>
        ) : (
          <div className="px-6 py-5">
            {/* Deviations Table */}
            {filteredDeviations.length > 0 ? (
              <>
                <div className="eg-card-elevated overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[var(--color-surface)] border-b border-[var(--color-border)]">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold eg-text-primary">Page</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold eg-text-primary">
                            Equipment Type
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold eg-text-primary">Label</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold eg-text-primary">Issue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--color-border)]">
                        {paginatedDeviations.map((dev, i) => (
                          <tr key={`${dev.equipment_type}-${dev.page_number}-${i}`} className="hover:bg-[var(--color-surface-hover)] transition-colors">
                            <td className="px-4 py-3 text-sm text-engramiq-green font-medium">
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  window.dispatchEvent(new CustomEvent('navigateToAsBuiltPage', {
                                    detail: {
                                      page: dev.page_number,
                                      boundingBox: dev.bounding_box ?? undefined,
                                    },
                                  }));
                                }}
                                onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLElement).click()}
                                className="cursor-pointer hover:bg-engramiq-green/20 hover:text-engramiq-green rounded px-1 -mx-1 transition-colors"
                              >
                                P{dev.page_number}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 bg-purple-400/20 text-purple-400 rounded text-sm">
                                {dev.equipment_type}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-mono text-sm eg-text-primary">{dev.label}</td>
                            <td className="px-4 py-3 text-sm eg-text-secondary">
                              {dev.issue}
                              {dev.expected && dev.actual && (
                                <div className="mt-1 text-xs">
                                  <span className="eg-text-secondary">Expected: </span>
                                  <span className="text-green-400">{dev.expected}</span>
                                  <span className="eg-text-secondary mx-1">|</span>
                                  <span className="eg-text-secondary">Actual: </span>
                                  <span className="text-red-400">{dev.actual}</span>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm eg-text-secondary">
                      Showing {startItem} to {endItem} of {totalItems} deviations
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handlePreviousPage}
                        disabled={offset === 0 || isLoading}
                        className="px-3 py-1.5 bg-[var(--color-bg-primary)] hover:bg-[var(--color-surface-hover)] border border-[var(--color-border)] hover:border-engramiq-green rounded-lg text-engramiq-green transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--color-surface)] disabled:hover:border-[var(--color-border)] flex items-center gap-1"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span>Previous</span>
                      </button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum: number;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              disabled={isLoading}
                              className={`px-3 py-1.5 rounded-lg transition-all ${
                                currentPage === pageNum
                                  ? 'bg-engramiq-green text-engramiq-dark-blue font-medium'
                                  : 'bg-[var(--color-bg-primary)] hover:bg-[var(--color-surface-hover)] eg-text-primary border border-[var(--color-border)] hover:border-engramiq-green'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={handleNextPage}
                        disabled={offset + itemsPerPage >= totalItems || isLoading}
                        className="px-3 py-1.5 bg-[var(--color-bg-primary)] hover:bg-[var(--color-surface-hover)] border border-[var(--color-border)] hover:border-engramiq-green rounded-lg text-engramiq-green transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--color-surface)] disabled:hover:border-[var(--color-border)] flex items-center gap-1"
                      >
                        <span>Next</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
                {totalPages <= 1 && totalItems > 0 && (
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm eg-text-secondary">
                      Showing {startItem} to {endItem} of {totalItems} deviations
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-green-400/20 border border-green-400/30 rounded-lg p-6 text-center">
                <p className="text-green-400">No deviations match your filters.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NomenclatureTab;
